import React from 'react';
import styled from 'styled-components';
import {ClickEventHandler, CmsButtonProps} from '../../elements/';
import {UeLink} from '../link/UeLink.wrap';
import {AccentColorOptions, useIsTwinmotionTheme} from 'epic-ue-theme';
import {getOptImage} from 'epic-ue-shared';
import {getLocale} from '@epic-core/common';

const MouseHijackWrapper = styled.div<{cursorUrl?: string; href?: string; showCTAIcon: boolean}>`
    & {
        position: relative;
    }
    &:after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-repeat: no-repeat;
        background-position: center center;
        background-image: url(${(p) => (p.showCTAIcon ? p.cursorUrl : '')});
        background-size: 120px 120px;
        transition: opacity 0.3s ${(p) => p.theme.easing};
    }
    &:hover {
        cursor: url(${(p) => p.cursorUrl}) 60 60, ${(p) => (p.href ? 'pointer' : 'auto')};
        &:after {
            opacity: 0;
        }
    }
`;

export type MouseHijackCursorOptions = 'play' | 'view';

export interface CmsButtonPropsWithHoverType extends CmsButtonProps {
    hoverCursorType?: MouseHijackCursorOptions;
}

const defaultCursor = 'https://cdn2.unrealengine.com/view-icon-sky-240x240-5031852475f0.png';

const textCursors = {
    sky: {
        play: 'https://cdn2.unrealengine.com/play-text-sky-240x240-944aca525fca.png',
        view: 'https://cdn2.unrealengine.com/view-text-sky-240x240-1623f63ed918.png'
    },
    forest: {
        play: 'https://cdn2.unrealengine.com/play-text-forest-240x240-210c4fb48dc2.png',
        view: 'https://cdn2.unrealengine.com/view-text-forest-240x240-0c60b627535a.png'
    },
    galaxy: {
        play: 'https://cdn2.unrealengine.com/play-text-galaxy-240x240-c2940da8cd6a.png',
        view: 'https://cdn2.unrealengine.com/view-text-galaxy-240x240-5fdb26dba620.png'
    },
    fire: {
        play: 'https://cdn2.unrealengine.com/play-text-fire-240x240-24b375106b14.png',
        view: 'https://cdn2.unrealengine.com/view-text-fire-240x240-8f9b12c99abd.png'
    },
    orchid: {
        play: 'https://cdn2.unrealengine.com/play-text-tw-120x120-c90168932bc2.png',
        view: 'https://cdn2.unrealengine.com/view-text-tw-120x120-f8938233136d.png'
    },
    neon: {
        play: 'https://cdn2.unrealengine.com/play-text-neon-240x240-9e1681eea8d0.png',
        view: 'https://cdn2.unrealengine.com/view-text-neon-240x240-ccb6ca0fc311.png'
    }
};

const iconCursors = {
    sky: {
        play: 'https://cdn2.unrealengine.com/play-icon-sky-240x240-082e4f37d928.png',
        view: 'https://cdn2.unrealengine.com/view-icon-sky-240x240-5031852475f0.png'
    },
    forest: {
        play: 'https://cdn2.unrealengine.com/play-icon-forest-240x240-2bad8a356911.png',
        view: 'https://cdn2.unrealengine.com/view-icon-forest-240x240-cb2587333aca.png'
    },
    galaxy: {
        play: 'https://cdn2.unrealengine.com/play-icon-galaxy-240x240-ee938801798e.png',
        view: 'https://cdn2.unrealengine.com/view-icon-galaxy-240x240-02845f90b11f.png'
    },
    fire: {
        play: 'https://cdn2.unrealengine.com/play-icon-fire-240x240-f8dd08511f2a.png',
        view: 'https://cdn2.unrealengine.com/view-icon-fire-240x240-56d16407958d.png'
    },
    orchid: {
        play: 'https://cdn2.unrealengine.com/play-icon-tw-120x120-19fe9ca646ee.png',
        view: 'https://cdn2.unrealengine.com/view-icon-tw-120x120-3aeb1ecf2d4c.png'
    },
    neon: {
        play: 'https://cdn2.unrealengine.com/play-icon-neon-240x240-d69eb64148ba.png',
        view: 'https://cdn2.unrealengine.com/view-icon-neon-240x240-215d896d8691.png'
    }
};

const getCursors = (locale: string, useIcons?: boolean) => {
    if (useIcons) return iconCursors;

    if (locale === 'en-US') {
        return textCursors;
    }

    return iconCursors;
};

export interface MouseHijackProps {
    accentColor?: AccentColorOptions;
    onClick?: ClickEventHandler;
    href?: string;
    children?: React.ReactNode;
    disable?: boolean;
    hoverCursorType?: MouseHijackCursorOptions;
    useIcons?: boolean;
    forceOverlay?: boolean;
}

export const MouseHijack = ({
    children,
    href,
    disable,
    onClick,
    accentColor = 'sky',
    hoverCursorType = 'view',
    useIcons = false,
    forceOverlay = false
}: MouseHijackProps): JSX.Element => {
    const locale = getLocale();
    const isTwinmotion = useIsTwinmotionTheme();
    const finalAccentColor = isTwinmotion ? 'orchid' : accentColor;

    if (disable || (!href && !onClick)) return <>{children}</>;

    const colored = getCursors(locale, useIcons)[finalAccentColor] || {};
    const cursor = getOptImage({
        url: colored[hoverCursorType] || defaultCursor,
        width: 120,
        height: 120,
        quality: 'high'
    });

    return (
        <UeLink href={href} onClick={onClick}>
            <MouseHijackWrapper
                href={href}
                cursorUrl={disable ? '' : cursor}
                showCTAIcon={forceOverlay || hoverCursorType === 'play'}>
                <div className="mouse-content">{children}</div>
            </MouseHijackWrapper>
        </UeLink>
    );
};
